/**
 * Copyright (C) 2018-present, Botobag Inc.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *      http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

import { Address } from './address';
import {
  CustomerData,
  Customer,
} from './customer';
import {
  DraftFulfillment,
  DraftFulfillmentData,
} from './draftFulfillment';
import {
  DraftOrderItem,
  DraftOrderItemData,
} from './draftOrderItem';
import {
  OrderPriceData,
  OrderPrice,
} from './orderPrice';
import { PaymentMethod } from './paymentMethod';

// DraftOrderData contains data for creating an Order. It can be used to store customer's cart or to
// render checkout page.
export interface DraftOrderData {
  id: string;

  currency: string;

  customer?: CustomerData;

  // List of items in the order
  items: DraftOrderItemData[];

  // List of fulfillments for delivering items in the order.
  fulfillments: DraftFulfillmentData[];

  // Billing address
  billingAddress?: Address;

  // Summary of price regard to the draft order
  price: OrderPriceData;

  // (Extension) Available payment methods
  availablePaymenMethods: PaymentMethod[];
}

export class DraftOrder {
  public readonly customer?: Customer;
  public readonly items: DraftOrderItem[];
  public readonly fulfillments: DraftFulfillment[];
  public readonly price: OrderPrice;
  public readonly totalQuantity: number;

  constructor(private data: DraftOrderData) {
    if (data.customer) {
      this.customer = new Customer(data.customer);
    }
    this.items = data.items.map((itemData) => new DraftOrderItem(data.currency, itemData));
    this.fulfillments = data.fulfillments.map(
      (fulfillment) => new DraftFulfillment(data.currency, this, fulfillment));
    this.price = new OrderPrice(data.currency, data.price);
    this.totalQuantity = this.items.reduce<number>((prev, cur) => (prev + cur.quantity), 0);
  }

  get id() { return this.data.id; }
  get currency() { return this.data.currency; }
}
